import { Build } from "~/components/build/Show/lib/types";
import { WATERFALL_MAX_JOBS } from "~/components/build/Show/lib/queries/waterfall";

const numberFormatter = new Intl.NumberFormat();

// Mirrors the row shape of the loading <Skeleton /> so the empty state reads
// as "this is what would have been here". Two parent rows followed by five
// child rows of varying widths and offsets.
const SVG_ROWS = [
  { left: 0, width: 100, child: false },
  { left: 50, width: 50, child: false },
  { left: 50, width: 22, child: true },
  { left: 50, width: 22, child: true },
  { left: 50, width: 26, child: true },
  { left: 50, width: 50, child: true },
  { left: 50, width: 50, child: true },
];

const SVG_WIDTH = 240;
const SVG_HEIGHT = 140;
const ROW_HEIGHT = SVG_HEIGHT / SVG_ROWS.length;
const LABEL_DIVIDER_X = 60;
const CHART_AREA_LEFT = 64;
const CHART_AREA_RIGHT = 236;
const CHART_AREA_WIDTH = CHART_AREA_RIGHT - CHART_AREA_LEFT;
// Use project palette (config/tailwind.config.ts) so the illustration sits
// quietly on top of the slate-100 empty state background. Borders use slate-400;
// bars step one shade lighter on the grey scale so they read as separate from
// the chart frame without raising contrast.
const FRAME_COLOR = "#e5e5e5"; // gray-400
const BAR_COLOR = "#e5e5e5"; // gray-400

function WaterfallIllustration() {
  return (
    <svg
      viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Waterfall chart illustration"
      className="block h-auto w-full max-w-sm"
    >
      {/* Outer rounded border. 0.5/SVG-1 trick keeps the 1px stroke crisp inside the viewBox. */}
      <rect
        x="0.5"
        y="0.5"
        width={SVG_WIDTH - 1}
        height={SVG_HEIGHT - 1}
        rx="6"
        ry="6"
        stroke={FRAME_COLOR}
      />

      {/* Vertical divider between the label column and the chart column */}
      <line x1={LABEL_DIVIDER_X} y1="0" x2={LABEL_DIVIDER_X} y2={SVG_HEIGHT} stroke={FRAME_COLOR} />

      {SVG_ROWS.map((row, index) => {
        const rowY = index * ROW_HEIGHT;
        const labelX = row.child ? 28 : 12;
        const barX = CHART_AREA_LEFT + (row.left / 100) * CHART_AREA_WIDTH;
        const barWidth = (row.width / 100) * CHART_AREA_WIDTH;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <g key={index}>
            {index > 0 && <line x1="0" y1={rowY} x2={SVG_WIDTH} y2={rowY} stroke={FRAME_COLOR} />}
            <rect
              x={labelX}
              y={rowY + ROW_HEIGHT / 2 - 2}
              width="14"
              height="4"
              rx="1"
              fill={BAR_COLOR}
            />
            <rect
              x={barX}
              y={rowY + ROW_HEIGHT / 2 - 3}
              width={barWidth}
              height="6"
              rx="1"
              fill={BAR_COLOR}
            />
          </g>
        );
      })}
    </svg>
  );
}

export default function TooManySteps({ build }: { build: Build }) {
  const jobsCount = build.statistics?.jobsCount;
  const jobsCountLabel = jobsCount
    ? numberFormatter.format(jobsCount)
    : `more than ${numberFormatter.format(WATERFALL_MAX_JOBS)}`;

  return (
    <div className="h-full w-full">
      <div className="rounded-3 relative flex h-full min-h-[160px] flex-col items-center justify-center gap-5 bg-slate-100 p-5 antialiased">
        <WaterfallIllustration />
        <div className="flex flex-col items-center justify-center gap-2.5">
          <h3 className="m-0 text-center text-lg">Too many jobs to render a waterfall</h3>
          <p className="m-0 max-w-[360px] text-center">
            This build has {jobsCountLabel} jobs, which is too many to render in the waterfall view.
            Try the canvas or table view instead, or{" "}
            <a href="mailto:support@buildkite.com">contact support</a> if you need any assistance.
          </p>
        </div>
      </div>
    </div>
  );
}
